import React from "react";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../../lib/utils";
import QuestionFrame from "./QuestionFrame";
import OtherSpecifyField from "./OtherSpecifyField";
import {
  OTHER_OPTION_ID,
  parseMultiChoice,
  serializeMultiChoice,
} from "../surveyUtils";

/**
 * Select-all-that-apply question, with optional Other + specify field.
 *
 * @param {Object} props
 * @param {object} props.question
 * @param {unknown} [props.value]
 * @param {function(unknown, {{ immediate?: boolean }}): void} props.onChange
 * @param {string} [props.error]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.showRequired]
 * @returns {JSX.Element}
 */
export default function MultiChoiceQuestion({
  question,
  value,
  onChange,
  error,
  readOnly,
  showRequired,
}) {
  const options = question.options || [];
  const allowOther = Boolean(question.allowOther);
  const { ids, otherText } = parseMultiChoice(value);
  const otherSelected = ids.includes(OTHER_OPTION_ID);

  /**
   * @param {string} optionId
   * @param {boolean} checked
   * @returns {void}
   */
  function toggle(optionId, checked) {
    if (readOnly) return;
    let nextIds = ids;
    if (checked) {
      nextIds = ids.includes(optionId) ? ids : [...ids, optionId];
    } else {
      nextIds = ids.filter((id) => id !== optionId);
    }
    const nextText = nextIds.includes(OTHER_OPTION_ID) ? otherText : "";
    onChange(serializeMultiChoice(nextIds, nextText), { immediate: true });
  }

  return (
    <QuestionFrame question={question} indent showRequired={showRequired}>
      <div className="space-y-1">
        {options.map((option) => {
          const checked = ids.includes(option.id);
          const optionId = `survey-q-${question.id}-${option.id}`;
          return (
            <label
              key={option.id}
              htmlFor={optionId}
              className={cn(
                "flex items-center gap-3 rounded-md py-2 transition-colors",
                readOnly ? "cursor-default" : "cursor-pointer hover:bg-ui-muted/50"
              )}
            >
              <Checkbox
                id={optionId}
                checked={checked}
                disabled={readOnly}
                onCheckedChange={(next) => toggle(option.id, next)}
              />
              <span className="text-sm text-text-primary">{option.label}</span>
            </label>
          );
        })}
        {allowOther ? (
          <div>
            <label
              htmlFor={`survey-q-${question.id}-other`}
              className={cn(
                "flex items-center gap-3 rounded-md py-2 transition-colors",
                readOnly ? "cursor-default" : "cursor-pointer hover:bg-ui-muted/50"
              )}
            >
              <Checkbox
                id={`survey-q-${question.id}-other`}
                checked={otherSelected}
                disabled={readOnly}
                onCheckedChange={(next) => toggle(OTHER_OPTION_ID, next)}
              />
              <span className="text-sm text-text-primary">Other</span>
            </label>
            {otherSelected ? (
              <OtherSpecifyField
                id={`survey-q-${question.id}-other-text`}
                value={otherText}
                readOnly={readOnly}
                error={error}
                onChange={(text) =>
                  onChange(serializeMultiChoice(ids, text), { immediate: false })
                }
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </QuestionFrame>
  );
}
